<template>
  <section id="news" class="news-section">
    <div class="container">
      <div class="section-title">
        <div class="en-label">{{ $t('news.eyebrow') }}</div>
        <h2>{{ $t('news.title') }}</h2>
        <p>{{ $t('news.desc') }}</p>
      </div>

      <div class="news-layout" v-if="featuredNews">
        <div class="news-feature" @click="showDetail(featuredNews)">
          <div class="feature-img" :style="{ background: featuredNews.bg }">
            <div class="feature-overlay">
              <el-tag class="news-cat" size="small">{{ featuredNews.category }}</el-tag>
            </div>
            <div class="feature-decor"><i :class="featuredNews.icon"></i></div>
          </div>
          <div class="feature-body">
            <div class="news-meta">
              <span>{{ featuredNews.date }}</span>
              <span>{{ featuredNews.readTime }}</span>
            </div>
            <h3>{{ featuredNews.title }}</h3>
            <p>{{ featuredNews.excerpt }}</p>
            <div class="read-more">{{ $t('news.readMore') }} <i class="el-icon-arrow-right"></i></div>
          </div>
        </div>

        <div class="news-list">
          <div class="news-item" v-for="item in newsList" :key="item.id" @click="showDetail(item)">
            <div class="news-img-sm" :style="{ background: item.bg }">
              <i :class="item.icon"></i>
            </div>
            <div class="news-body">
              <el-tag size="mini" class="news-cat-sm">{{ item.category }}</el-tag>
              <h4>{{ item.title }}</h4>
              <div class="news-meta">
                <span>{{ item.date }}</span>
                <span>{{ item.readTime }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="insights-row">
        <div class="insight-card" v-for="ins in insights" :key="ins.id">
          <div class="ins-icon" :style="{ background: ins.color }"><i :class="ins.icon"></i></div>
          <h4>{{ ins.title }}</h4>
          <p>{{ ins.desc }}</p>
          <a class="ins-link">{{ ins.link }} <i class="el-icon-arrow-right"></i></a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getNewsItems, localizeNewsItems } from '@/data/newsStore'

export default {
  name: 'NewsSection',
  data() {
    return {
      newsItems: []
    }
  },
  computed: {
    localizedNews() {
      return localizeNewsItems(this.newsItems, this.$i18n.locale)
    },
    insights() {
      return this.$tm('news.insights')
    },
    sortedNews() {
      return [...this.localizedNews].sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    featuredNews() {
      return this.sortedNews.find(item => item.featured) || this.sortedNews[0]
    },
    newsList() {
      if (!this.featuredNews) return []
      return this.sortedNews.filter(item => item.id !== this.featuredNews.id).slice(0, 4)
    }
  },
  mounted() {
    this.newsItems = getNewsItems()
  },
  methods: {
    showDetail(item) {
      this.$message.info(this.$t('news.detailComingSoon', { title: item.title }))
    }
  }
}
</script>

<style lang="less" scoped>
.news-section { padding: 120px 0; background: #fff; }
.news-layout { display: grid; grid-template-columns: 1fr 420px; gap: 28px; margin-bottom: 48px; }
.news-feature { border-radius: var(--radius-lg); overflow: hidden; border: 1px solid #eee; cursor: pointer; transition: box-shadow .25s, transform .25s; }
.news-feature:hover { box-shadow: var(--card-shadow-hover); transform: translateY(-4px); }
.feature-img { height: 220px; position: relative; display: flex; align-items: center; justify-content: center; }
.feature-overlay { position: absolute; top: 16px; left: 16px; }
.news-cat { background: rgba(108,92,231,.3) !important; color: #a29bfe !important; border: 1px solid rgba(162,155,254,.3) !important; border-radius: 20px !important; font-size: 12px !important; }
.feature-decor { font-size: 60px; color: rgba(255,255,255,.08); }
.feature-body { padding: 28px; }
.feature-body h3 { font-size: 20px; font-weight: 800; color: var(--text-dark); line-height: 1.4; margin-bottom: 12px; }
.feature-body p { font-size: 14px; color: var(--text-mid); line-height: 1.8; margin-bottom: 20px; }
.news-meta { display: flex; gap: 12px; font-size: 12px; color: var(--text-light); margin-bottom: 12px; }
.read-more { font-size: 14px; color: var(--primary); font-weight: 600; display: flex; align-items: center; gap: 4px; }
.news-list { display: flex; flex-direction: column; gap: 12px; }
.news-item { display: flex; gap: 16px; padding: 16px; border-radius: var(--radius); border: 1px solid #eee; cursor: pointer; transition: all .2s; }
.news-item:hover { background: var(--bg-light); border-color: #d0e8f5; transform: translateX(4px); }
.news-img-sm { width: 70px; height: 70px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 24px; color: rgba(255,255,255,.6); }
.news-cat-sm { background: #eef4f9 !important; color: #0a5c8a !important; border: none !important; border-radius: 10px !important; font-size: 10px !important; margin-bottom: 6px !important; }
.news-body h4 { font-size: 14px; font-weight: 700; color: var(--text-dark); line-height: 1.4; margin-bottom: 8px; }
.insights-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; }
.insight-card { padding: 28px; background: var(--bg-light); border-radius: var(--radius-lg); border: 1px solid #eee; transition: all .2s; }
.insight-card:hover { transform: translateY(-4px); box-shadow: var(--card-shadow); background: #fff; }
.insight-card h4 { font-size: 16px; font-weight: 700; color: var(--text-dark); margin: 14px 0 10px; }
.insight-card p { font-size: 13px; color: var(--text-mid); line-height: 1.7; margin-bottom: 16px; }
.ins-icon { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; }
.ins-link { font-size: 13px; color: var(--primary); font-weight: 600; cursor: pointer; }
@media (max-width: 1000px) { .news-layout { grid-template-columns: 1fr; } }
@media (max-width: 900px) { .insights-row { grid-template-columns: 1fr; } }
</style>
